import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { postApi, storageApi, adApi, uploadApi } from '../utils/api';
import { Post, Reply, File as FileType, Ad } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { zhCN, enUS } from 'date-fns/locale';
import { Eye, Heart, MessageCircle, Edit, Trash2, Download, Paperclip, Upload } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { t } from '../stores/languageStore';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import { getNicknameColor, getRoleLabel } from '../utils/roleUtils';
import AdDisplay from '../components/AdDisplay';
import ZoomableImage from '../components/ZoomableImage';

const PostDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, isAuthenticated } = useAuthStore();
  const [post, setPost] = React.useState<Post | null>(null);
  const [replies, setReplies] = React.useState<Reply[]>([]);
  const [attachments, setAttachments] = React.useState<FileType[]>([]);
  const [replyAttachments, setReplyAttachments] = React.useState<FileType[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [replyContent, setReplyContent] = React.useState('');
  const [replyingTo, setReplyingTo] = React.useState<number | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [uploadingAttachment, setUploadingAttachment] = React.useState(false);
  const [liked, setLiked] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [ads, setAds] = React.useState<Record<string, Ad[]>>({});
  const [adsLoading, setAdsLoading] = React.useState(true);

  const fetchPost = React.useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await postApi.getPostById(parseInt(id));
      if (response.success && response.data) {
        setPost(response.data.post);
        setReplies(response.data.replies);
        setAttachments(response.data.attachments || []);
      }
    } catch (error) {
      console.error('Failed to fetch post:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchAds = React.useCallback(async () => {
    try {
      setAdsLoading(true);
      const response = await adApi.getActiveAds();
      if (response.success && response.data) {
        setAds(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch ads:', error);
    } finally {
      setAdsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchPost();
    fetchAds();
  }, [fetchPost, fetchAds]);

  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { 
        addSuffix: true, 
        locale: zhCN 
      });
    } catch {
      return dateString;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileExt: string) => {
    const ext = fileExt.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) {
      return '🖼️';
    } else if (['pdf'].includes(ext)) {
      return '📄';
    } else if (['doc', 'docx'].includes(ext)) {
      return '📝';
    } else if (['xls', 'xlsx'].includes(ext)) {
      return '📊';
    } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
      return '📦';
    } else if (['txt'].includes(ext)) {
      return '📃';
    } else {
      return '📁';
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      toast.error(t('auth.pleaseLogin'));
      return;
    }
    if (!post) return;

    try {
      const response = await postApi.likePost(post.id);
      if (response.success && response.data) {
        setLiked(response.data.liked);
        setPost({
          ...post,
          likes: response.data.liked ? post.likes + 1 : post.likes - 1,
        });
      }
    } catch (error) {
      console.error('Failed to like post:', error);
    }
  };

  const handleReply = async () => {
    if (!isAuthenticated) {
      toast.error(t('auth.pleaseLogin'));
      return;
    }
    if (!replyContent.trim() && replyAttachments.length === 0) {
      toast.error(t('posts.writeReply'));
      return;
    }
    if (!post) return;

    try {
      setSubmitting(true);
      const attachmentIds = replyAttachments.map((a) => a.id);
      const response = await postApi.createReply(post.id, {
        content: replyContent,
        parent_id: replyingTo,
        attachment_ids: attachmentIds,
      });
      if (response.success) {
        toast.success(t('posts.replySuccess'));
        setReplyContent('');
        setReplyAttachments([]);
        setReplyingTo(null);
        fetchPost();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('posts.replyFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadReplyAttachment = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error(t('posts.attachmentTooLarge'));
      e.target.value = '';
      return;
    }

    try {
      setUploadingAttachment(true);
      const response = await uploadApi.uploadAttachment(file);
      if (response.success && response.data) {
        setReplyAttachments((prev) => [...prev, response.data]);
        toast.success(t('posts.attachmentUploadSuccess'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('posts.attachmentUploadFailed'));
    } finally {
      setUploadingAttachment(false);
      e.target.value = '';
    }
  };

  const removeReplyAttachment = (index: number) => {
    setReplyAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const confirmDeletePost = async () => {
    if (!post) return;
    try {
      const response = await postApi.deletePost(post.id);
      if (response.success) {
        toast.success(t('posts.deleteSuccess'));
        setShowDeleteModal(false);
        window.history.back();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('posts.deleteFailed'));
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleDeleteReply = async (replyId: number) => {
    if (!confirm(t('posts.confirmDeleteReply'))) return;
    try {
      const response = await postApi.deleteReply(replyId);
      if (response.success) {
        toast.success(t('posts.deleteSuccess'));
        fetchPost();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('posts.deleteFailed'));
    }
  };

  const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const response = await uploadApi.uploadImage(file);
      if (response.success && response.data) {
        const imageUrl = response.data.url;
        const markdown = `![${file.name}](${imageUrl})`;
        setReplyContent((prev) => prev + '\n' + markdown);
        toast.success(t('posts.imageUploadSuccess'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('posts.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t('posts.notFound')}</p>
      </div>
    );
  }

  const isAuthor = user?.id === post.author_id;
  const isAdmin = user?.role === 'admin' || user?.role === 'moderator';

  return (
    <div className="space-y-6">
      {/* 帖子内容 */}
      <article className="card p-4 md:p-6">
        {/* 标题 */}
        <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">{post.title}</h1>

        {/* 元信息 */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 pb-4 border-b border-gray-100 gap-4">
          <div className="flex items-center gap-3">
            <Link to={`/user/${post.author_id}`}>
              <img
                src={post.author_avatar || `https://ui-avatars.com/api/?name=${post.author_name}&background=3b82f6&color=fff`}
                alt={post.author_name}
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${post.author_name}&background=3b82f6&color=fff`;
                }}
              />
            </Link>
            <div>
              <Link
                to={`/user/${post.author_id}`}
                className={`font-medium hover:text-primary-600 ${getNicknameColor(post.author_role || '普通用户')}`}
              >
                {getRoleLabel(post.author_role || '普通用户')}{post.author_name}
              </Link>
              {post.author_level_icon && <span className="ml-1">{post.author_level_icon}</span>}
              {post.author_titles && <span className="ml-1 text-xs text-purple-600">{post.author_titles}</span>}
              <div className="text-xs sm:text-sm text-gray-500">
                Lv.{post.author_level} · {formatDate(post.created_at)}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 text-xs sm:text-sm text-gray-500">
            <span className="flex items-center">
              <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              {post.views}
            </span>
            <span className="flex items-center">
              <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
              {post.replies_count}
            </span>
          </div>
        </div>

        {/* 内容 */}
        <div className="markdown-content prose max-w-none">
          <ReactMarkdown
            components={{
              img: ({ ...props }) => <ZoomableImage {...props} />,
            }}
          >
            {post.content}
          </ReactMarkdown>
        </div>

        {/* 附件列表 */}
        {attachments.length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Paperclip className="w-5 h-5 mr-2" />
              {t('posts.attachments')} ({attachments.length})
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {attachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                            <span className="text-2xl">{getFileIcon(attachment.file_ext)}</span>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-800 truncate">{attachment.filename}</p>
                              <p className="text-sm text-gray-500">
                                {attachment.file_size_formatted} · {attachment.download_count} {t('posts.downloadCount')}
                              </p>
                            </div>
                          </div>
                  <a
                    href={storageApi.downloadFile(attachment.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-2 bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span className="text-sm">{t('posts.download')}</span>
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 操作按钮 */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-4">
            <button
              onClick={handleLike}
              className={`flex items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
                liked
                  ? 'bg-red-50 text-red-600'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} />
              <span>{post.likes}</span>
            </button>
          </div>

          {(isAuthor || isAdmin) && (
            <div className="flex items-center gap-2">
              {isAuthor && (
                <Link
                  to={`/edit-post/${post.id}`}
                  className="btn btn-secondary text-sm"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  {t('posts.edit')}
                </Link>
              )}
              <button
                onClick={handleDeleteClick}
                className="btn btn-danger text-sm"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {t('posts.delete')}
              </button>
            </div>
          )}
        </div>
      </article>

      {/* 帖子详情页广告位 */}
      {!adsLoading && ads.post_detail && ads.post_detail.length > 0 && (
        <div className="card p-4">
          <AdDisplay ads={ads.post_detail} />
        </div>
      )}

      {/* 回复列表 */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t('posts.replies')} ({post.replies_count})
        </h2>

        {replies.length > 0 ? (
          <div className="space-y-4">
            {replies.map((reply) => (
              <div key={reply.id} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                <Link to={`/user/${reply.author_id}`} className="flex-shrink-0">
                  <img
                    src={reply.author_avatar || `https://ui-avatars.com/api/?name=${reply.author_name}&background=3b82f6&color=fff`}
                    alt={reply.author_name}
                    className="w-8 h-8 rounded-full"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${reply.author_name}&background=3b82f6&color=fff`;
                    }}
                  />
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Link
                      to={`/user/${reply.author_id}`}
                      className={`font-medium hover:text-primary-600 text-sm ${getNicknameColor(reply.author_role || '普通用户')}`}
                    >
                      {getRoleLabel(reply.author_role || '普通用户')}{reply.author_name}
                    </Link>
                    {reply.author_level_icon && <span className="text-sm">{reply.author_level_icon}</span>}
                    {reply.author_titles && <span className="text-xs text-purple-600">{reply.author_titles}</span>}
                    <span className="text-xs text-gray-400">Lv.{reply.author_level}</span>
                    <span className="text-xs text-gray-400">·</span>
                    <span className="text-xs text-gray-500">{formatDate(reply.created_at)}</span>
                  </div>
                  <div className="markdown-content prose max-w-none text-sm">
                    <ReactMarkdown
                      components={{
                        img: ({ ...props }) => <ZoomableImage {...props} />,
                      }}
                    >
                      {reply.content}
                    </ReactMarkdown>
                  </div>
                  
                  {/* 回复附件 */}
                  {reply.attachments && reply.attachments.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex flex-wrap gap-2">
                        {reply.attachments.map((attachment) => (
                          <a
                            key={attachment.id}
                            href={storageApi.downloadFile(attachment.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            <span>{getFileIcon(attachment.file_ext)}</span>
                            <span className="text-xs text-gray-700 truncate max-w-[120px]">{attachment.filename}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4 mt-2">
                    <button
                      onClick={() => setReplyingTo(reply.id)}
                      className="text-xs text-gray-500 hover:text-primary-600"
                    >
                      {t('posts.reply')}
                    </button>
                    {((user?.id === reply.author_id) || isAdmin) && (
                      <button
                        onClick={() => handleDeleteReply(reply.id)}
                        className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        {t('posts.delete')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">{t('posts.noReplies')}</p>
        )}
      </div>

      {/* 回复框 */}
      {isAuthenticated ? (
        <div className="card p-6">
          <h3 className="font-semibold text-gray-900 mb-4">
            {replyingTo ? t('posts.replyComment') : t('posts.postReply')}
          </h3>
          {replyingTo && (
            <div className="mb-2 text-sm text-gray-500">
              {t('posts.replyingTo')} #{replyingTo}
              <button
                onClick={() => setReplyingTo(null)}
                className="ml-2 text-primary-600 hover:underline"
              >
                {t('posts.cancel')}
              </button>
            </div>
          )}
          <div className="flex items-center justify-between mb-1">
            <div className="flex gap-2">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUploadImage}
                  className="hidden"
                  disabled={uploading}
                />
                <span className="btn btn-secondary text-sm inline-flex items-center">
                  <Upload className="w-4 h-4 mr-1" />
                  {uploading ? t('posts.uploading') : t('posts.uploadImage')}
                </span>
              </label>
              <label className="cursor-pointer">
                <input
                  type="file"
                  onChange={handleUploadReplyAttachment}
                  className="hidden"
                  disabled={uploadingAttachment}
                />
                <span className="btn btn-secondary text-sm inline-flex items-center">
                  <Paperclip className="w-4 h-4 mr-1" />
                  {uploadingAttachment ? t('posts.uploading') : t('posts.uploadAttachment')}
                </span>
              </label>
            </div>
          </div>
          
          {/* 回复附件列表 */}
          {replyAttachments.length > 0 && (
            <div className="mb-3">
              <p className="text-sm text-gray-500 mb-2">{t('posts.attachmentsAdded')} ({replyAttachments.length})</p>
              <div className="flex flex-wrap gap-2">
                {replyAttachments.map((attachment, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg"
                  >
                    <span className="text-sm text-gray-700 truncate max-w-[150px]">
                      {attachment.filename}
                    </span>
                    <button
                      onClick={() => removeReplyAttachment(index)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder={t('posts.writeReply')}
            className="input min-h-[120px] resize-none"
          />
          <div className="flex justify-end mt-4">
            <button
              onClick={handleReply}
              disabled={submitting}
              className="btn btn-primary"
            >
              {submitting ? t('posts.sending') : t('posts.sendReply')}
            </button>
          </div>
        </div>
      ) : (
        <div className="card p-6 text-center">
          <p className="text-gray-500">
            <Link to="/login" className="link">
              {t('posts.login')}
            </Link>{' '}
            {t('posts.loginToJoin')}
          </p>
        </div>
      )}

      {/* 帖子详情页底部广告位 */}
      {!adsLoading && ads.post_detail_bottom && ads.post_detail_bottom.length > 0 && (
        <div className="card p-4">
          <AdDisplay ads={ads.post_detail_bottom} />
        </div>
      )}

      {/* 删除确认模态框 */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h2 className="text-xl font-bold mb-4">{t('posts.confirmDelete')}</h2>
            <p className="mb-4">{t('posts.confirmDeletePost')}</p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
              >
                {t('posts.cancel')}
              </button>
              <button
                onClick={confirmDeletePost}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
              >
                {t('posts.confirmDelete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostDetail;
